import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ReportsContext = createContext(null);

const DEFAULT_FILTERS = {
    period: 'this_week',
    startDate: null,
    endDate: null,
    department: 'all',
    teamMember: 'all',
    projectType: 'all',
    projectStatus: 'all',
    taskStatus: 'all',
    priority: 'all',
    projectId: 'all',
};

export const ReportsProvider = ({ children }) => {
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [activeTab, setActiveTab] = useState('overview');
    const [refreshKey, setRefreshKey] = useState(0);

    // In-memory client cache per tab keyed by filter fingerprint
    const tabCacheRef = useRef({});

    const getFilterFingerprint = useCallback((f) => {
        return JSON.stringify({
            period: f.period,
            startDate: f.startDate,
            endDate: f.endDate,
            department: f.department,
            teamMember: f.teamMember,
            projectType: f.projectType,
            projectStatus: f.projectStatus,
            taskStatus: f.taskStatus,
            priority: f.priority,
            projectId: f.projectId,
        });
    }, []);

    const getCachedTab = useCallback((tabKey) => {
        const cached = tabCacheRef.current[tabKey];
        if (!cached) return null;
        const currentFingerprint = getFilterFingerprint(filters);
        if (cached.fingerprint === currentFingerprint) {
            return cached.data;
        }
        return null;
    }, [filters, getFilterFingerprint]);

    const setCachedTab = useCallback((tabKey, data) => {
        const currentFingerprint = getFilterFingerprint(filters);
        tabCacheRef.current[tabKey] = {
            fingerprint: currentFingerprint,
            data,
            cachedAt: Date.now(),
        };
    }, [filters, getFilterFingerprint]);

    const clearCache = useCallback(() => {
        tabCacheRef.current = {};
    }, []);

    const updateFilter = useCallback((key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const resetFilters = useCallback(() => {
        clearCache();
        setFilters(DEFAULT_FILTERS);
        setRefreshKey(k => k + 1);
    }, [clearCache]);

    const triggerRefresh = useCallback(() => {
        clearCache();
        setRefreshKey(k => k + 1);
    }, [clearCache]);

    const switchTabWithFilter = useCallback((tabKey, newFilters = {}) => {
        if (Object.keys(newFilters).length > 0) {
            setFilters(prev => ({ ...prev, ...newFilters }));
        }
        setActiveTab(tabKey);
    }, []);

    return (
        <ReportsContext.Provider
            value={{
                filters,
                activeTab,
                setActiveTab,
                updateFilter,
                resetFilters,
                triggerRefresh,
                switchTabWithFilter,
                refreshKey,
                getCachedTab,
                setCachedTab,
                clearCache,
            }}
        >
            {children}
        </ReportsContext.Provider>
    );
};

export const useReports = () => {
    const context = useContext(ReportsContext);
    if (!context) {
        throw new Error('useReports must be used within a ReportsProvider');
    }
    return context;
};
